import { useState, useCallback, useEffect, useMemo } from "react";
import { useMutation, gql } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import { Container, Stack, Paper, Typography, Button } from "@mui/material";

import type { Policy } from "../../../@lib/types";

import { useAuthSession } from "../../../components/AuthSessionProvider";
import { useNotify } from "../../../components/@common/NotificationsProvider";
import {
  CardAll,
  ConditionalRenderer,
  Header,
  TabsFilters,
  CTAContainer,
  useLanguageTranslator,
} from "../../../components";
import { usePrivatePoliciesCall } from "../../../hooks";

const policyDeleteMutationDocument = gql`
  mutation policyDeleteMutation($policy: PolicyDeleteInput!) {
    policyDelete(input: $policy) {
      policy {
        id
      }
    }
  }
`;

function PrivatePoliciesList() {
  const [inputValue, setInputValue] = useState("");
  const [currTab, setCurrTab] = useState<"all" | any>("all");
  const [limit, setLimit] = useState(8);

  const notify = useNotify();
  const { user } = useAuthSession() as any;
  const isLoggedIn = !!user;
  const { translator } = useLanguageTranslator();

  const [deletePolicy, { loading: deletingPolicy }] = useMutation(
    policyDeleteMutationDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const { policies, dist, loading, refetch, metadata }: any =
    usePrivatePoliciesCall({
      user,
      policyStatus: currTab,
      variables: { limit: limit },
    });

  function onClickHandler() {
    const scrollPosition = window.scrollY;
    setLimit(limit + 8);

    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  }

  /**
   * handleDeletePolicy()
   */
  const handleDeletePolicy = useCallback(
    async (policy: Pick<Policy, "id" | "title">) => {
      if (
        await notify.confirm(
          `Deleting policy "${policy.title}". This cannot be undone.`
        )
      ) {
        try {
          await deletePolicy({ variables: { policy: { id: policy.id } } });
          notify.success(`Policy Deleted Successfully`);
          refetch();
        } catch (err) {
          notify.error(err as Error);
        }
      }
    },
    [deletePolicy, refetch, notify]
  );

  const privateTab = [
    {
      value: "all",
      count: metadata?.totalCount,
      title: translator("All"),
    },
    {
      value: "approved",
      count: dist.approved.length,
      title: translator("status.Approved"),
    },
    {
      value: "pending",
      count: dist.pending.length,
      title: translator("status.Pending"),
    },
    {
      value: "rejected",
      count: dist.rejected.length,
      title: translator("status.Rejected"),
    },
  ];

  const hasMore =
    limit === metadata?.totalCount ||
    limit > metadata?.totalCount ||
    !metadata?.totalCount;

  const filteredPoliciesList = useMemo(() => {
    if (!inputValue) return policies || [];

    const searchValue = inputValue
      ?.normalize("NFD")
      ?.replace(/[\u0300-\u036f]/g, "")
      ?.toLowerCase();

    return policies?.filter((item: any) =>
      item?.title
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(searchValue)
    );
  }, [inputValue, policies]);

  return (
    <div>
      <Header
        title={translator("Policies")}
        subtitle=""
        setInputValue={setInputValue}
        inputValue={inputValue}
      />

      <Container sx={{ my: 3 }}>
        <TabsFilters
          dist={dist}
          currTab={currTab}
          setCurrTab={setCurrTab}
          listItems={privateTab}
          heading={`${translator("List of")} ${translator("Policies")}`}
          isAllShow={true}
        />

        <Stack gap={2}>
          {filteredPoliciesList?.map((p: any, i: any) => (
            <CardAll
              p={p}
              loading={loading || deletingPolicy}
              path={`/policies/${p.id}`}
              isLoggedIn={isLoggedIn}
              editPath={`/policies/${p?.id}/edit`}
              handleDelete={handleDeletePolicy}
            />
          ))}
        </Stack>
      </Container>

      <ConditionalRenderer condition={!hasMore}>
        <Container sx={{ my: 3 }}>
          <Paper sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <Button onClick={onClickHandler}>{translator("Show more")}</Button>
          </Paper>
        </Container>
      </ConditionalRenderer>

      {isLoggedIn && (
        <Container sx={{ my: 3 }}>
          <CTAContainer sx={{ textAlign: "center" }}>
            <Typography variant="subtitle1" mb={2}>
              Add new policies to the platform
            </Typography>
            <Button
              component={RouterLink}
              to="/submission/policies/new"
              sx={{
                px: 6,
                backgroundColor: "#ffffff",
                color: "primary.dark",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              }}
            >
              {translator("generic.CREATE")}
            </Button>
          </CTAContainer>
        </Container>
      )}
    </div>
  );
}

export { PrivatePoliciesList };
